import * as React from 'react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material"
import Space from "../../../../components/Space"
import PlanModel from "../../../../models/plan.model"
import { handlePromise } from "../../../../utils/handlePromisse"
import { getPlans } from "../../../../services/plan-service"
import { DcoEmailsRequest } from "../../../../services/dco-emails/dco-emails.service"

export type DcoFormHandle = {
    submit: () => void;
    isValid: boolean;
}

type Props = {
    tags: string[]
    onDeleteTag: (tag: string) => void
    onAdded: (gifts: DcoEmailsRequest[]) => void
    onValidityChange?: (valid: boolean) => void
}

export const DcoForm = forwardRef<DcoFormHandle, Props>(({ tags, onAdded, onDeleteTag, onValidityChange }, ref) => {
    const [tag, setTag] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [selectedEmails, setSelectedEmails] = useState<string[]>([])

    const [plan, setPlan] = useState('')
    const [plans, setPlans] = useState<PlanModel[]>([])

    const hasEmails = selectedEmails.length > 0 || email.trim().length > 0
    const isValid = hasEmails && !!plan && tag.length > 0

    useEffect(() => {
        onValidityChange?.(isValid)
    }, [isValid, onValidityChange])

    const fetchPlans = useCallback(async () => {
        const [response, error] = await handlePromise(getPlans())
        if (!!error) {
            console.log('error', error, error.stack)
            return
        }
        setPlans(response.data as PlanModel[])
    }, [selectedEmails])

    useEffect(() => {
        fetchPlans()
    }, [])

    const handleAddEmail = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && email && !selectedEmails.includes(email)) {
            event.preventDefault()

            const newEmail = email.split(/,|\s+|\n/)
            if (newEmail.length > 0) {
                const filtered = newEmail.filter((e) => !selectedEmails.includes(e.trim()))
                setSelectedEmails((emails) => [...emails, ...filtered])
            } else {
                setSelectedEmails((emails) => [...emails, email.trim()])
            }

            setEmail('')
        }
    }, [selectedEmails, email, setEmail])

    const handleRemoveEmail = useCallback((emailToRemove: string) => {
        setSelectedEmails((emails) => emails.filter((e) => e !== emailToRemove))
    }, [])

    const handleSend = useCallback(() => {
        const pending = email
            .split(/,|\s+|\n/)
            .map((e) => e.trim())
            .filter((e) => e && !selectedEmails.includes(e))
        const finalEmails = [...selectedEmails, ...pending]

        const emails = finalEmails.map((e) => {
            return {
                email: e,
                plan: parseInt(plan),
                tag: tag
            } as DcoEmailsRequest
        })

        onAdded(emails)

        setSelectedEmails([])
        setEmail('')
        setPlan('')
        setTag('')
    }, [selectedEmails, email, plan, tag, onAdded])

    useImperativeHandle(ref, () => ({
        submit: handleSend,
        isValid,
    }), [handleSend, isValid])

    const renderTags = useCallback(() => {
        return selectedEmails.map((option, index) => (
            <Chip
                key={index}
                label={option}
                color={'primary'}
                onDelete={() => handleRemoveEmail(option)}
            />
        ))
    }, [handleRemoveEmail, selectedEmails])

    return (
        <>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'inherit', opacity: 0.7 }}>
                Deixe planos específicos visíveis apenas para e-mails cadastrados. Use tags para filtrar grupos de usuários.
            </p>

            <Autocomplete
                multiple
                freeSolo
                value={selectedEmails}
                renderTags={renderTags}
                options={[]}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        label={'E-mails'}
                        variant="outlined"
                        color="primary"
                        onKeyDown={handleAddEmail}
                        onChange={(e) => setEmail(e.target.value)}
                        InputProps={{ ...params.InputProps }}
                        helperText="Pressione Enter para adicionar cada e-mail"
                    />
                )}
            />

            <Space pixels={16} direction={'v'}/>

            <FormControl fullWidth>
                <InputLabel id="dco-plan-select-label">Plano</InputLabel>
                <Select
                    labelId="dco-plan-select-label"
                    id="dco-plan-select"
                    value={plan}
                    onChange={(event) => setPlan(event.target.value)}
                    label="Plano">
                    {
                        plans.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))
                    }
                </Select>
            </FormControl>

            <Space pixels={16} direction={'v'}/>

            <TextField
                value={tag}
                label={'Tag'}
                color="primary"
                variant="outlined"
                fullWidth
                onChange={(e) => setTag(e.target.value)}
            />
        </>
    )
})

DcoForm.displayName = 'DcoForm'
